/*
 * The site header (sections/tcs-header.liquid).
 *
 *   Scroll ......... material once content passes beneath; out of the way while reading down on a phone;
 *                    publishes its visible height as --tcs-header-offset for sticky things below it.
 *   Panels ......... one shared surface beneath the bar. Opening grows it from the bar; moving between
 *                    items resizes it and cross-fades the contents in the direction of travel; the page
 *                    behind dims. Hover intent ignores a pointer passing over an item on its way down
 *                    into the open panel.
 *   Glide .......... one highlight that moves between the bar's items rather than each lighting up.
 *   Keyboard ....... arrows move along the bar, ArrowDown opens and enters a panel, Tab runs from an
 *                    item into its panel and on to the next item, Escape closes and returns focus.
 *   Menu ........... levels that slide in and out like an app; Back returns; closing resets to the top.
 *   Search ......... / or Ctrl/⌘+K opens it; arrows move through results; recent searches; clear button.
 *   Chat ........... the header icon stands in for Tidio's floating bubble.
 *
 * Dialogs (menu, search) open and close through tcs.js (data-tcs-open, window.tcs).
 */
(() => {
  const header = document.querySelector('[data-tcs-header]');
  if (!header || header.tcsReady) return;
  header.tcsReady = true;

  const root = document.documentElement;
  const desktop = window.matchMedia('(min-width: 1024px)');
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const focusables = (el) =>
    [...el.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]), [tabindex]:not([tabindex="-1"])')].filter(
      (node) => !node.closest('[hidden]') && node.offsetParent !== null,
    );
  const isTyping = (el) => el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));

  /* ---------------------------------------------------------------- Glass */

  // The lens (an SVG filter on the backdrop) only renders in Chromium, and costs more than a blur, so it
  // is kept for desktop-class devices there. Everyone else gets the same glass without the bend.
  const chromium = !!navigator.userAgentData?.brands?.some((b) => /Chromium/i.test(b.brand));
  const capable = (navigator.hardwareConcurrency || 2) >= 4 && window.matchMedia('(pointer: fine)').matches;
  const plain = window.matchMedia('(prefers-reduced-transparency: reduce)');
  const setLens = () => root.classList.toggle('tcs-refract', chromium && capable && !plain.matches);
  setLens();
  plain.addEventListener?.('change', setLens);

  // Clear over the homepage hero until the hero has scrolled out from under the bar.
  const hero = header.classList.contains('tcs-header--over-hero') ? document.querySelector('main > .shopify-section:first-child .tcs-hero') : null;
  // A homepage that doesn't open with the hero keeps the ordinary bar.
  if (!hero) header.classList.remove('tcs-header--over-hero');
  const checkHero = () => {
    if (!hero) return;
    const bar = header.querySelector('.tcs-header__bar').offsetHeight;
    const left = hero.getBoundingClientRect().bottom <= bar + 1;
    header.toggleAttribute('data-left-hero', left);
  };

  // Dark over a deep band (the closing section's): the glass takes the band's tone and the contents turn
  // white, instead of a white pane going grey over the dark. Measured at the middle of the bar.
  const darkBands = [...document.querySelectorAll('.tcs-closing-dark, [data-tcs-dark]')];
  const checkDark = () => {
    const mid = header.querySelector('.tcs-header__bar').offsetHeight / 2;
    const over = darkBands.some((band) => {
      const box = band.getBoundingClientRect();
      return box.top <= mid && box.bottom >= mid;
    });
    header.toggleAttribute('data-over-dark', over);
    // The browser's toolbar follows the bar: dark over the hero and the deep band, the page's colour otherwise.
    const meta = document.querySelector('meta[data-tcs-theme-color]');
    const overHero = hero && !header.hasAttribute('data-left-hero');
    if (meta) meta.content = over ? '#1d1b18' : overHero ? (hero.classList.contains('tcs-hero--light') ? getComputedStyle(hero).backgroundColor : '#12110f') : '#f8f8f7';
  };

  /* ---------------------------------------------------------------- Scroll */

  let lastY = window.scrollY;
  let travelled = 0;
  const logo = header.querySelector('[data-tcs-logo] .tcs-header__logo-art') ? header.querySelector('[data-tcs-logo]') : null;

  // On the homepage, choosing the logo while scrolled down glides back to the top instead of reloading.
  header.querySelector('[data-tcs-logo]')?.addEventListener('click', (event) => {
    const home = new URL(event.currentTarget.href, location.href);
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    if (home.pathname !== location.pathname || window.scrollY < 4) return;
    event.preventDefault();
    window.scrollTo({ top: 0, behavior: reduce.matches ? 'auto' : 'smooth' });
  });
  const publishOffset = () => {
    const hidden = header.hasAttribute('data-hidden') && !desktop.matches && !reduce.matches;
    root.style.setProperty('--tcs-header-offset', `${hidden ? 0 : header.querySelector('.tcs-header__bar').offsetHeight}px`);
  };
  const onScroll = () => {
    const y = Math.max(0, window.scrollY);
    header.toggleAttribute('data-scrolled', y > 4);
    const delta = y - lastY;
    lastY = y;
    // Stay put while something in the header is in use.
    if (header.hasAttribute('data-mega-open') || header.contains(document.activeElement) || document.querySelector('dialog[open]')) {
      header.removeAttribute('data-hidden');
    } else {
      // Wait for a deliberate movement, so a small wobble doesn't flicker the bar.
      travelled = Math.sign(delta) === Math.sign(travelled) ? travelled + delta : delta;
      if (y < header.offsetHeight * 2 || travelled < -12) header.removeAttribute('data-hidden');
      else if (travelled > 24) header.setAttribute('data-hidden', '');
    }
    // The logo folds to its mark once the page is under way, and unfolds near the top. The gap
    // between the two thresholds keeps it from flickering on a slow scroll around one point.
    if (logo) {
      if (y > 220) header.setAttribute('data-folded', '');
      else if (y < 120) header.removeAttribute('data-folded');
    }
    checkHero();
    checkDark();
    publishOffset();
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', () => { checkHero(); checkDark(); }, { passive: true });
  // The basket's confirmation asks for the bar, so it has something to come from.
  document.addEventListener('tcs:reveal-header', () => {
    header.removeAttribute('data-hidden');
    publishOffset();
  });
  window.addEventListener('resize', publishOffset, { passive: true });
  onScroll();

  /* ---------------------------------------------------------------- Glide */

  const nav = header.querySelector('[data-tcs-nav]');
  const glide = header.querySelector('[data-tcs-glide]');
  const navItems = nav ? [...nav.querySelectorAll('.tcs-header__nav-link')] : [];
  let glideOn = null;
  const moveGlide = (item) => {
    if (!glide) return;
    if (!item) {
      glide.removeAttribute('data-visible');
      glideOn = null;
      return;
    }
    const navBox = nav.getBoundingClientRect();
    const box = item.getBoundingClientRect();
    // Arriving from nothing it appears in place; between items it travels.
    glide.toggleAttribute('data-instant', !glideOn);
    glide.style.setProperty('--x', `${box.left - navBox.left}px`);
    glide.style.setProperty('--w', `${box.width}px`);
    glide.setAttribute('data-visible', '');
    glideOn = item;
  };
  const settleGlide = () => moveGlide(openPanel?.trigger || (nav?.contains(document.activeElement) && document.activeElement.matches(':focus-visible') ? document.activeElement : null));
  navItems.forEach((item) => {
    item.addEventListener('pointerenter', () => canHover.matches && moveGlide(item));
    item.addEventListener('focus', () => item.matches(':focus-visible') && moveGlide(item));
    item.addEventListener('blur', () => requestAnimationFrame(settleGlide));
  });
  nav?.addEventListener('pointerleave', () => settleGlide());

  /* ---------------------------------------------------------------- Panels */

  const surface = header.querySelector('[data-tcs-mega-surface]');
  const scrim = header.parentElement.querySelector('[data-tcs-header-scrim]');
  const triggers = [...header.querySelectorAll('[data-tcs-mega-trigger]')];
  const panelFor = (trigger) => document.getElementById(trigger.getAttribute('aria-controls'));
  let openPanel = null;
  let hoverTimer;
  let closeTimer;
  const sizer = 'ResizeObserver' in window ? new ResizeObserver(() => openPanel && setHeight(openPanel.panel)) : null;

  const setHeight = (panel) => surface.style.setProperty('--mega-h', `${panel.offsetHeight}px`);
  const animate = (el, name) => {
    el.removeAttribute('data-anim');
    void el.offsetWidth; // restart the animation if the same one runs twice
    el.setAttribute('data-anim', name);
  };

  const closePanel = (immediately = false) => {
    if (!openPanel) return;
    const { trigger, panel } = openPanel;
    openPanel = null;
    sizer?.disconnect();
    trigger.setAttribute('aria-expanded', 'false');
    header.removeAttribute('data-mega-open');
    scrim?.removeAttribute('data-visible');
    settleGlide();
    const finish = () => {
      if (openPanel) return;
      panel.hidden = true;
      panel.removeAttribute('data-anim');
      surface.removeAttribute('data-open');
    };
    if (immediately || reduce.matches) {
      surface.style.setProperty('--mega-h', '0px');
      finish();
      return;
    }
    animate(panel, 'out');
    surface.style.setProperty('--mega-h', '0px');
    clearTimeout(closeTimer);
    closeTimer = setTimeout(finish, 340);
  };

  const openPanelFor = (trigger) => {
    if (openPanel?.trigger === trigger) return;
    const panel = panelFor(trigger);
    if (!panel || !surface) return;
    clearTimeout(closeTimer);
    const previous = openPanel;
    previous?.trigger.setAttribute('aria-expanded', 'false');

    panel.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    header.setAttribute('data-mega-open', '');
    scrim?.setAttribute('data-visible', '');
    openPanel = { trigger, panel };
    moveGlide(trigger);
    sizer?.disconnect();
    sizer?.observe(panel);

    if (previous && previous.panel !== panel) {
      // Moving along the bar: the contents slide the way the pointer went, the surface resizes.
      const forward = Number(panel.dataset.index) > Number(previous.panel.dataset.index);
      const old = previous.panel;
      animate(old, forward ? 'leave-left' : 'leave-right');
      setTimeout(() => {
        if (openPanel?.panel !== old) {
          old.hidden = true;
          old.removeAttribute('data-anim');
        }
      }, 200);
      animate(panel, forward ? 'enter-right' : 'enter-left');
      setHeight(panel);
    } else {
      // From closed: the surface grows down from the bar and the contents arrive just after.
      surface.setAttribute('data-open', '');
      if (!surface.style.getPropertyValue('--mega-h')) surface.style.setProperty('--mega-h', '0px');
      animate(panel, 'in');
      requestAnimationFrame(() => setHeight(panel));
    }
  };

  // Pictures load before they're needed: as soon as the pointer or focus reaches the bar, so a panel
  // never opens onto empty tiles. The menu's do the same when it opens.
  const warm = (scope) => scope?.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
  let warmed = false;
  const warmPanels = () => {
    if (warmed) return;
    warmed = true;
    warm(surface);
  };
  nav?.addEventListener('pointerenter', warmPanels, { once: true });
  nav?.addEventListener('focusin', warmPanels, { once: true });

  // Where the pointer has been lately, to tell "moving along the bar" from "heading down into the panel".
  let trail = [];
  header.addEventListener('pointermove', (event) => {
    const now = performance.now();
    trail.push({ x: event.clientX, y: event.clientY, t: now });
    trail = trail.filter((p) => now - p.t < 90);
  });
  const headingDown = () => {
    if (trail.length < 2) return false;
    const a = trail[0];
    const b = trail[trail.length - 1];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    return dy > 2 && dy > Math.abs(dx) * 0.6;
  };

  triggers.forEach((trigger) => {
    trigger.addEventListener('click', (event) => {
      clearTimeout(hoverTimer);
      if (openPanel?.trigger === trigger) {
        // A click right after hover opened it is the same intent, not a second press.
        if (performance.now() - (trigger.tcsOpenedAt || 0) > 400) closePanel();
      } else {
        openPanelFor(trigger);
        trigger.tcsOpenedAt = event.detail === 0 ? 0 : performance.now();
      }
    });
    trigger.addEventListener('pointerenter', () => {
      if (!canHover.matches || !desktop.matches) return;
      clearTimeout(hoverTimer);
      clearTimeout(closeTimer);
      if (!openPanel) {
        // Resting on an item opens it; a pointer passing over doesn't.
        hoverTimer = setTimeout(() => {
          openPanelFor(trigger);
          trigger.tcsOpenedAt = performance.now();
        }, 110);
        return;
      }
      const delay = headingDown() ? 220 : 40;
      hoverTimer = setTimeout(() => {
        if (trigger.matches(':hover')) openPanelFor(trigger);
      }, delay);
    });
    trigger.addEventListener('pointerleave', () => !openPanel && clearTimeout(hoverTimer));

    trigger.addEventListener('keydown', (event) => {
      const panel = panelFor(trigger);
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        openPanelFor(trigger);
        requestAnimationFrame(() => focusables(panel)[0]?.focus());
      } else if (event.key === 'Tab' && !event.shiftKey && openPanel?.trigger === trigger) {
        // The panel follows its item in the reading order, though it sits after the bar in the page.
        const first = focusables(panel)[0];
        if (first) {
          event.preventDefault();
          first.focus();
        }
      }
    });
  });

  // Arrows along the bar, wrapping at the ends.
  nav?.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    const index = navItems.indexOf(document.activeElement);
    if (index < 0) return;
    event.preventDefault();
    const next = navItems[(index + (event.key === 'ArrowRight' ? 1 : -1) + navItems.length) % navItems.length];
    next.focus();
    if (openPanel && next.hasAttribute('data-tcs-mega-trigger')) openPanelFor(next);
    else if (openPanel) closePanel();
  });

  surface?.addEventListener('keydown', (event) => {
    if (!openPanel) return;
    const items = focusables(openPanel.panel);
    const { trigger } = openPanel;
    if (event.key === 'Tab' && !event.shiftKey && document.activeElement === items[items.length - 1]) {
      // Out of the end of a panel: on to the item after the one that opened it.
      event.preventDefault();
      const after = navItems[navItems.indexOf(trigger) + 1];
      closePanel();
      (after || header.querySelector('.tcs-header__logo')).focus();
    } else if (event.key === 'Tab' && event.shiftKey && document.activeElement === items[0]) {
      event.preventDefault();
      trigger.focus();
    } else if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && items.includes(document.activeElement)) {
      event.preventDefault();
      const i = items.indexOf(document.activeElement) + (event.key === 'ArrowDown' ? 1 : -1);
      if (i < 0) trigger.focus();
      else items[Math.min(i, items.length - 1)].focus();
    }
  });

  header.addEventListener('pointerleave', () => {
    clearTimeout(hoverTimer);
    if (!canHover.matches || !openPanel) return;
    hoverTimer = setTimeout(() => closePanel(), 260);
  });
  header.addEventListener('pointerenter', () => openPanel && clearTimeout(hoverTimer));
  scrim?.addEventListener('click', () => closePanel());
  // Arriving on the dimmed page (however quickly the pointer got there) means the panel is done with.
  scrim?.addEventListener('pointerenter', () => {
    if (!canHover.matches || !openPanel) return;
    clearTimeout(hoverTimer);
    hoverTimer = setTimeout(() => closePanel(), 200);
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && openPanel) {
      const { trigger } = openPanel;
      closePanel();
      trigger.focus();
    }
  });
  document.addEventListener('click', (event) => {
    if (openPanel && !header.contains(event.target)) closePanel();
  });
  header.addEventListener('focusout', (event) => {
    if (openPanel && event.relatedTarget && !header.contains(event.relatedTarget)) closePanel();
  });
  desktop.addEventListener('change', () => closePanel(true));
  // A link followed from a panel: close it at once, so the page that arrives isn't dimmed.
  surface?.addEventListener('click', (event) => event.target.closest('a[href]') && closePanel(true));

  /* ---------------------------------------------------------------- Menu levels */

  const menu = document.getElementById('tcs-menu');
  const pages = menu ? [...menu.querySelectorAll('[data-tcs-menu-page]')] : [];
  const back = menu?.querySelector('[data-tcs-menu-back]');
  const title = menu?.querySelector('[data-tcs-menu-title]');
  const body = menu?.querySelector('.tcs-menu__body');
  let level = pages[0];
  let levelTimer;

  const showLevel = (target, direction) => {
    if (!target || target === level) return;
    const from = level;
    level = target;
    clearTimeout(levelTimer);
    pages.forEach((page) => page !== from && page !== target && (page.hidden = true, page.removeAttribute('data-anim')));
    target.hidden = false;
    if (body) body.scrollTop = 0;
    title.textContent = target.dataset.title;
    back.hidden = target === pages[0];
    if (reduce.matches) {
      from.hidden = true;
    } else {
      animate(from, direction === 'forward' ? 'leave-left' : 'leave-right');
      animate(target, direction === 'forward' ? 'enter-right' : 'enter-left');
      levelTimer = setTimeout(() => {
        from.hidden = true;
        from.removeAttribute('data-anim');
      }, 320);
    }
    requestAnimationFrame(() => (target === pages[0] ? back : focusables(target)[0] || back)?.focus({ preventScroll: true }));
  };

  menu?.addEventListener('click', (event) => {
    const go = event.target.closest('[data-tcs-menu-go]');
    if (go) {
      const target = pages.find((page) => page.dataset.tcsMenuPage === go.dataset.tcsMenuGo);
      if (target) target.dataset.from = go.dataset.tcsMenuGo;
      showLevel(target, 'forward');
      return;
    }
    if (event.target.closest('[data-tcs-menu-back]')) {
      const returning = level;
      showLevel(pages[0], 'back');
      requestAnimationFrame(() => menu.querySelector(`[data-tcs-menu-go="${returning.dataset.tcsMenuPage}"]`)?.focus({ preventScroll: true }));
      return;
    }
    if (event.target.closest('[data-tcs-menu-search]')) {
      window.tcs?.close(menu);
      setTimeout(() => window.tcs?.open('tcs-search', header.querySelector('[data-tcs-open="tcs-search"]')), 160);
    }
  });
  // A level follows the finger back to the menu, as in an app: drag it right and the menu beneath
  // comes forward; let go past a third of the way (or with a flick) and it goes, otherwise it returns.
  const pagesBox = menu?.querySelector('[data-tcs-menu-pages]');
  let swipe = null;
  const clearSwipe = (page) => {
    ['data-settling', 'data-swiping', 'data-swipe-under'].forEach((name) => page.removeAttribute(name));
    page.style.translate = '';
    page.style.opacity = '';
  };
  pagesBox?.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'touch' || level === pages[0] || reduce.matches) return;
    swipe = { x: event.clientX, y: event.clientY, t: event.timeStamp, dx: 0, active: false, id: event.pointerId };
  });
  pagesBox?.addEventListener('pointermove', (event) => {
    if (!swipe || event.pointerId !== swipe.id) return;
    const dx = event.clientX - swipe.x;
    const dy = event.clientY - swipe.y;
    if (!swipe.active) {
      if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) {
        swipe = null; // a scroll, not a swipe
        return;
      }
      if (dx < 12 || dx < Math.abs(dy) * 1.5) return;
      swipe.active = true;
      swipe.page = level;
      swipe.under = pages[0];
      clearTimeout(levelTimer);
      swipe.under.hidden = false;
      swipe.under.removeAttribute('data-anim');
      swipe.under.setAttribute('data-swipe-under', '');
      swipe.page.removeAttribute('data-anim');
      swipe.page.setAttribute('data-swiping', '');
      pagesBox.setPointerCapture?.(event.pointerId);
    }
    swipe.dx = Math.max(0, dx);
    const progress = Math.min(1, swipe.dx / pagesBox.offsetWidth);
    swipe.page.style.translate = `${swipe.dx}px 0`;
    swipe.under.style.translate = `${-28 + 28 * progress}% 0`;
    swipe.under.style.opacity = String(0.3 + 0.7 * progress);
  });
  const endSwipe = (event) => {
    if (!swipe || event.pointerId !== swipe.id) return;
    const s = swipe;
    swipe = null;
    if (!s.active) return;
    const velocity = s.dx / Math.max(1, event.timeStamp - s.t);
    const done = event.type !== 'pointercancel' && (s.dx > pagesBox.offsetWidth * 0.33 || (velocity > 0.45 && s.dx > 40));
    s.page.setAttribute('data-settling', '');
    s.under.setAttribute('data-settling', '');
    requestAnimationFrame(() => {
      s.page.style.translate = done ? '100% 0' : '0 0';
      s.under.style.translate = done ? '0 0' : '-28% 0';
      s.under.style.opacity = done ? '1' : '0';
    });
    setTimeout(() => {
      clearSwipe(s.page);
      clearSwipe(s.under);
      if (done) {
        s.page.hidden = true;
        level = pages[0];
        title.textContent = pages[0].dataset.title;
        back.hidden = true;
        menu.querySelector(`[data-tcs-menu-go="${s.page.dataset.tcsMenuPage}"]`)?.focus({ preventScroll: true });
      } else {
        s.under.hidden = true;
      }
    }, 220);
  };
  pagesBox?.addEventListener('pointerup', endSwipe);
  pagesBox?.addEventListener('pointercancel', endSwipe);

  // Backspace or Alt+Left steps back a level, as in an app.
  menu?.addEventListener('keydown', (event) => {
    if (level === pages[0] || isTyping(event.target)) return;
    if (event.key === 'Backspace' || (event.altKey && event.key === 'ArrowLeft')) {
      event.preventDefault();
      back.click();
    }
  });
  menu?.addEventListener('close', () => {
    setTimeout(() => {
      pages.forEach((page, i) => {
        page.hidden = i !== 0;
        page.removeAttribute('data-anim');
      });
      level = pages[0];
      if (title) title.textContent = pages[0].dataset.title;
      if (back) back.hidden = true;
    }, 300);
  });
  menu?.addEventListener('tcs:open', () => {
    closePanel(true);
    warm(menu);
  });

  /* ---------------------------------------------------------------- Search */

  const search = document.getElementById('tcs-search');
  const input = search?.querySelector('[data-tcs-search-input]');
  const results = search?.querySelector('[data-tcs-search-results]');
  const empty = results?.querySelector('[data-tcs-search-empty]');
  const clear = search?.querySelector('[data-tcs-search-clear]');
  const recentBox = search?.querySelector('[data-tcs-search-recent]');
  const recentList = search?.querySelector('[data-tcs-search-recent-list]');
  const suggestUrl = (window.routes && window.routes.predictive_search_url) || '/search/suggest';
  const searchUrl = search?.querySelector('form')?.getAttribute('action') || '/search';
  const RECENT = 'tcs-recent-searches';
  let controller;
  let debounce;

  const readRecent = () => {
    try {
      return JSON.parse(localStorage.getItem(RECENT) || '[]').filter((term) => typeof term === 'string').slice(0, 5);
    } catch (error) {
      return [];
    }
  };
  const remember = (term) => {
    term = (term || '').trim();
    if (term.length < 2) return;
    try {
      localStorage.setItem(RECENT, JSON.stringify([term, ...readRecent().filter((t) => t.toLowerCase() !== term.toLowerCase())].slice(0, 5)));
    } catch (error) {}
  };
  const showRecent = () => {
    if (!recentBox) return;
    const terms = readRecent();
    recentBox.hidden = terms.length === 0;
    recentList.replaceChildren(
      ...terms.map((term) => {
        const link = document.createElement('a');
        link.className = 'tcs-button tcs-button--compact';
        link.href = `${searchUrl}?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last`;
        link.textContent = term;
        return link;
      }),
    );
  };
  search?.querySelector('[data-tcs-search-recent-clear]')?.addEventListener('click', () => {
    try {
      localStorage.removeItem(RECENT);
    } catch (error) {}
    showRecent();
    input?.focus();
  });

  const setLoading = (on) => {
    results?.toggleAttribute('data-loading', on);
    search?.toggleAttribute('data-loading', on);
  };

  const render = async (terms) => {
    controller?.abort();
    clear.hidden = !terms;
    if (!terms) {
      setLoading(false);
      results.querySelector('[data-tcs-search-rendered]')?.remove();
      if (empty) empty.hidden = false;
      showRecent();
      return;
    }
    controller = new AbortController();
    setLoading(true);
    const params = new URLSearchParams({
      q: terms,
      section_id: 'tcs-predictive-search',
      'resources[type]': 'product,collection,page,article',
      'resources[limit]': '6',
      'resources[options][unavailable_products]': 'last',
    });
    try {
      const response = await fetch(`${suggestUrl}?${params}`, { signal: controller.signal });
      if (!response.ok) throw new Error(response.status);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const rendered = html.querySelector('[data-tcs-search-rendered]');
      if (!rendered) return;
      if (empty) empty.hidden = true;
      results.querySelector('[data-tcs-search-rendered]')?.remove();
      results.append(rendered);
    } catch (error) {
      if (error.name === 'AbortError') return;
      // Suggestions are a convenience: if they fail, pressing Enter still searches.
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };

  input?.addEventListener('input', () => {
    clear.hidden = !input.value;
    clearTimeout(debounce);
    debounce = setTimeout(() => render(input.value.trim()), 160);
  });
  clear?.addEventListener('click', () => {
    input.value = '';
    render('');
    input.focus();
  });
  search?.querySelector('form')?.addEventListener('submit', () => remember(input.value));
  results?.addEventListener('click', (event) => {
    if (event.target.closest('a[href]') && input.value.trim()) remember(input.value);
  });

  // In the picture grid, arrows move as they look: left and right along a row, up and down by a row.
  search?.addEventListener('keydown', (event) => {
    const card = document.activeElement?.closest?.('.tcs-search__card');
    if (!card || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
    const cards = [...results.querySelectorAll('.tcs-search__card')];
    const columns = getComputedStyle(card.closest('.tcs-search__grid')).gridTemplateColumns.split(' ').length;
    const i = cards.indexOf(card);
    const step = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -columns, ArrowDown: columns }[event.key];
    const next = i + step;
    if (event.key === 'ArrowUp' && next < 0) {
      event.preventDefault();
      event.stopImmediatePropagation();
      input.focus();
      return;
    }
    if (next >= 0 && next < cards.length) {
      event.preventDefault();
      event.stopImmediatePropagation();
      cards[next].focus();
    } else if (event.key === 'ArrowDown') {
      // Past the last row: on to the collections and guides below.
      event.preventDefault();
      event.stopImmediatePropagation();
      const after = [...results.querySelectorAll('a[href]')].filter((link) => !link.closest('.tcs-search__grid'));
      after[0]?.focus();
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  });

  // Arrows run from the field down through the results and back.
  search?.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const links = [...results.querySelectorAll('a[href]')].filter((link) => !link.closest('[hidden]'));
    if (!links.length) return;
    const index = links.indexOf(document.activeElement);
    if (document.activeElement === input) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        links[0].focus();
      }
      return;
    }
    if (index < 0) return;
    event.preventDefault();
    if (event.key === 'ArrowUp' && index === 0) input.focus();
    else links[Math.max(0, Math.min(links.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1)))].focus();
  });

  search?.addEventListener('tcs:open', () => {
    closePanel(true);
    showRecent();
    // Give the sheet a frame to appear before the keyboard does.
    requestAnimationFrame(() => {
      input?.focus();
      input?.select();
    });
  });

  // "/" anywhere outside a field, or Ctrl/⌘+K, opens search.
  document.addEventListener('keydown', (event) => {
    if (!search || search.open) return;
    const slash = event.key === '/' && !event.metaKey && !event.ctrlKey && !event.altKey && !isTyping(event.target);
    const commandK = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k';
    if (!slash && !commandK) return;
    if (document.querySelector('dialog[open]')) return;
    event.preventDefault();
    window.tcs?.open('tcs-search', header.querySelector('[data-tcs-open="tcs-search"]'));
  });

  /* ---------------------------------------------------------------- Chat */

  // Tidio stays hidden (CSS, html.tcs-chat-managed) until a chat link is used; then it is opened, and
  // closing it hides it again. Visibility is only ever the CSS class (html.tcs-chat-open), never Tidio's
  // own hide()/show(): those apply a moment later, and a late hide() (from Tidio arriving on its own just
  // before a click) closed a chat the visitor had just opened. If Tidio hasn't loaded, the click loads it; if it never
  // arrives, the link's own destination (the contact page) is the fallback.
  const chatLinks = document.querySelectorAll('[data-tcs-chat]');
  if (chatLinks.length) {
    root.classList.add('tcs-chat-managed');
    const api = () => window.tidioChatApi;
    let ready = false;
    let pending = null;
    let fallback;

    // Unread replies: an ember badge on every chat entry point (the bar's chat icon, the phone's menu
    // button, the menu's Chat row), a number from 2 up. Tidio keeps its own count in this browser's
    // storage (tidio_state_<key>.unreadMessages), so the badge is right on every page from first paint,
    // before Tidio has loaded; once it has, the count follows Tidio live. Opening the chat clears it.
    const dots = () => document.querySelectorAll('[data-tcs-chat-dot]');
    const iconLinks = [...chatLinks].filter((link) => link.hasAttribute('aria-label'));
    iconLinks.forEach((link) => (link.dataset.tcsLabel = link.getAttribute('aria-label')));
    const storedUnread = () => {
      try {
        const key = Object.keys(localStorage).find((k) => /^tidio_state_[a-z0-9]+$/.test(k));
        const count = key ? JSON.parse(localStorage.getItem(key))?.unreadMessages : 0;
        const stored = Number.isFinite(count) ? count : 0;
        // Messages already seen in the open chat don't count again, should Tidio keep its number after a
        // read (tcs_chat_seen is the count when the chat was last closed).
        let seen = Number(localStorage.getItem('tcs_chat_seen')) || 0;
        if (stored < seen) {
          seen = stored;
          localStorage.setItem('tcs_chat_seen', String(stored));
        }
        return stored - seen;
      } catch (error) {
        return 0;
      }
    };
    const markSeen = () => {
      try {
        const key = Object.keys(localStorage).find((k) => /^tidio_state_[a-z0-9]+$/.test(k));
        const count = key ? JSON.parse(localStorage.getItem(key))?.unreadMessages : 0;
        localStorage.setItem('tcs_chat_seen', String(Number.isFinite(count) ? count : 0));
      } catch (error) {}
    };
    let unread = 0;
    const setUnread = (count) => {
      const next = root.classList.contains('tcs-chat-open') ? 0 : Math.max(0, Math.floor(count) || 0);
      if (next === unread) return;
      const arrived = next > unread;
      unread = next;
      dots().forEach((dot) => {
        dot.hidden = !next;
        dot.textContent = next > 1 ? (next > 9 ? '9+' : String(next)) : '';
        dot.toggleAttribute('data-count', next > 1);
        if (arrived) {
          dot.removeAttribute('data-pop');
          void dot.offsetWidth;
          dot.setAttribute('data-pop', '');
        }
      });
      iconLinks.forEach((link) => {
        const label = link.dataset.tcsLabel;
        link.setAttribute('aria-label', next ? `${label}, ${next} unread ${next === 1 ? 'message' : 'messages'}` : label);
      });
    };
    setUnread(storedUnread());
    // Another tab read or received a message.
    window.addEventListener('storage', (event) => {
      if (event.key && event.key.startsWith('tidio_state_')) setUnread(storedUnread());
    });

    const showAndOpen = () => {
      clearTimeout(fallback);
      pending?.classList.remove('is-loading');
      pending = null;
      root.classList.add('tcs-chat-open');
      // The configurator section hides Tidio on phones with inline styles; take them off for an opened chat.
      document.querySelectorAll('#tidio-chat, #tidio-chat-iframe, #tidio-chat-root, iframe[src*="tidio.co"]').forEach((node) => {
        ['display', 'visibility', 'pointer-events', 'opacity'].forEach((prop) => {
          if (/^(none|hidden|0)$/.test(node.style.getPropertyValue(prop))) node.style.removeProperty(prop);
        });
      });
      try {
        api().show();
        api().open();
      } catch (error) {}
    };
    const onReady = () => {
      if (ready || !api()) return;
      ready = true;
      try {
        api().on('close', () => {
          markSeen();
          root.classList.remove('tcs-chat-open');
        });
        api().on('messageFromOperator', () => {
          setUnread(Math.max(unread + 1, storedUnread()));
          setTimeout(() => setUnread(Math.max(unread, storedUnread())), 600);
        });
        // Tidio writes its count to storage as it syncs (replies sent while the visitor was away arrive
        // a moment after it loads), and writes in this tab raise no event: look again now and then.
        setUnread(storedUnread());
        setInterval(() => {
          if (document.visibilityState === 'visible' && !root.classList.contains('tcs-chat-open')) setUnread(storedUnread());
        }, 4000);
      } catch (error) {}
      if (pending) showAndOpen();
    };
    document.addEventListener('tidioChat-ready', onReady);
    if (api()?.readyEventWasFired) onReady();

    // Tidio is about 520 KB and often takes 5–8 seconds on a phone. So: start fetching it the moment a
    // chat link is reached for, open it whenever it arrives (however late), and never move the visitor
    // away by themselves. Only a script that fails, or one still missing after 15 s, brings an offer of
    // the contact page, as a button in the toast. (An earlier 4.5 s timeout said "not available" and then
    // redirected, so a slow chat seemed to open and vanish.)
    const offerContact = (link, title) => {
      link.classList.remove('is-loading');
      window.tcs?.toast?.({
        title,
        description: 'You can reach us on our contact page instead.',
        tone: 'info',
        duration: 8000,
        action: { label: 'Contact page', href: link.href },
      });
    };
    document.addEventListener('tcs:tidio-failed', () => {
      if (!pending) return;
      clearTimeout(fallback);
      const link = pending;
      pending = null;
      offerContact(link, 'Chat isn’t available just now');
    });
    chatLinks.forEach((link) => {
      ['pointerenter', 'focus', 'touchstart'].forEach((type) =>
        link.addEventListener(type, () => window.tcsLoadTidio?.(), { once: true, passive: true }),
      );
      link.addEventListener('click', (event) => {
        event.preventDefault();
        setUnread(0);
        closePanel(true);
        window.tcs?.close(link.closest('dialog'));
        if (ready && api()) return showAndOpen();
        pending = link;
        link.classList.add('is-loading');
        window.tcsLoadTidio?.();
        clearTimeout(fallback);
        fallback = setTimeout(() => {
          // Still loading: say so once, but keep waiting; it opens if it arrives.
          if (pending) offerContact(pending, 'Chat is taking a moment');
        }, 15000);
      });
    });
  }
})();
